import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { Box, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import { Loader, BlurredContactAdvisorBox } from 'ui';
import { ScoreWidget, ScoreWidgetMainColumn, ScoreWidgetSingleValueColumn } from 'ui/ScoreWidget';
import { formatDate } from 'shared/functions';
import { useReportPage } from 'shared/hooks';

const useStyles = makeStyles(theme => ({
  contactAdvisorDesc: {
    marginTop: theme.spacing(0.6),
    width: '100%',
  },
  infoIcon: {
    fontSize: 16,
    marginLeft: 3,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
}));

const ESGImpactScore = ({ data, score, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  if (loading)
    return (
      <Box height={150} display="flex" alignItems="center" justifyContent="center">
        <Loader center />
      </Box>
    );

  return data && score ? (
    <ScoreWidget>
      <ScoreWidgetMainColumn
        label={t('ESG Impact review')}
        value={score ? score.toFixed(1) : '-'}
      />
      <ScoreWidgetSingleValueColumn
        label={
          <div className={classes.label}>
            {t('Stability')}{' '}
            {!useReportPage && (
              <Tooltip
                placement="top"
                title={t(
                  'Sensitivity to shifts in business model leading to shifts in ESG impacts under consideration of data precision',
                )}
              >
                <InfoIcon className={classes.infoIcon} />
              </Tooltip>
            )}
          </div>
        }
        value={data.stability}
      />
      <ScoreWidgetSingleValueColumn
        label={
          <div className={classes.label}>
            {t('Data precision')}{' '}
            {!useReportPage && (
              <Tooltip
                placement="top"
                title={t('Indicator reflecting the quality and granularity of input data')}
              >
                <InfoIcon className={classes.infoIcon} />
              </Tooltip>
            )}
          </div>
        }
        value={data.dataPrecision}
      />
      <ScoreWidgetSingleValueColumn
        label={<Typography className={classes.label}>{t('Latest update')}</Typography>}
        value={formatDate(data.latestUpdate)}
        last
      />
    </ScoreWidget>
  ) : (
    <BlurredContactAdvisorBox active={false} />
  );
};

ESGImpactScore.defaultProps = {
  score: null,
  data: null,
};

ESGImpactScore.propTypes = {
  loading: PropTypes.bool.isRequired,
  score: PropTypes.number,
  data: PropTypes.shape({
    stability: PropTypes.string.isRequired,
    dataPrecision: PropTypes.string.isRequired,
    latestUpdate: PropTypes.string.isRequired,
  }),
};

export default ESGImpactScore;
